import { useEffect } from 'react';

interface SEOMetaProps {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogImage?: string;
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleAuthor?: string;
  articleSection?: string;
  articleTags?: string[];
  structuredData?: object | object[];
}

export default function SEOMeta({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogImage,
  articlePublishedTime,
  articleModifiedTime,
  articleAuthor,
  articleSection,
  articleTags,
  structuredData,
}: SEOMetaProps) {
  useEffect(() => {
    document.title = title;

    const setMeta = (name: string, content: string, attr: 'name' | 'property' = 'name') => {
      let el = document.querySelector(`meta[${attr}="${name}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    setMeta('description', description);
    if (keywords) setMeta('keywords', keywords);
    setMeta('og:title', title, 'property');
    setMeta('og:description', description, 'property');
    setMeta('og:type', ogType, 'property');
    setMeta('og:site_name', '五一吃瓜网', 'property');
    setMeta('og:locale', 'zh_CN', 'property');
    if (ogImage) setMeta('og:image', ogImage, 'property');
    if (canonical) {
      let linkEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
      if (!linkEl) {
        linkEl = document.createElement('link');
        linkEl.rel = 'canonical';
        document.head.appendChild(linkEl);
      }
      linkEl.href = canonical;
    }
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', title);
    setMeta('twitter:description', description);
    if (ogImage) setMeta('twitter:image', ogImage);

    if (articlePublishedTime) {
      setMeta('article:published_time', articlePublishedTime, 'property');
    }
    if (articleModifiedTime) {
      setMeta('article:modified_time', articleModifiedTime, 'property');
    }
    if (articleAuthor) {
      setMeta('article:author', articleAuthor, 'property');
    }
    if (articleSection) {
      setMeta('article:section', articleSection, 'property');
    }
    if (articleTags && articleTags.length > 0) {
      articleTags.forEach((tag) => {
        const existing = document.querySelectorAll('meta[property="article:tag"]');
        existing.forEach((e) => e.remove());
      });
      articleTags.forEach((tag) => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'article:tag');
        meta.content = tag;
        document.head.appendChild(meta);
      });
    }

    let scriptEl = document.getElementById('seo-structured-data') as HTMLScriptElement | null;
    if (structuredData) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'seo-structured-data';
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      const data = Array.isArray(structuredData) ? structuredData : [structuredData];
      scriptEl.textContent = JSON.stringify(data.length === 1 ? data[0] : data);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    return () => {
      // cleanup not needed for SEO meta
    };
  }, [
    title,
    description,
    keywords,
    canonical,
    ogType,
    ogImage,
    articlePublishedTime,
    articleModifiedTime,
    articleAuthor,
    articleSection,
    articleTags,
    structuredData,
  ]);

  return null;
}